import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSearch } from 'react-icons/md';
import { api } from '../services/api';

const QUICK_SPECS = [
  { label: 'General Physician', icon: '🩺' },
  { label: 'Cardiology',         icon: '❤️' },
  { label: 'Dermatology',        icon: '🧴' },
  { label: 'Pediatrics',         icon: '🧒' },
  { label: 'Orthopedic',         icon: '🦴' },
  { label: 'Gynecology',         icon: '🌷' },
  { label: 'ENT',                icon: '👂' },
  { label: 'Dental',             icon: '🦷' },
  { label: 'Neurology',          icon: '🧠' },
  { label: 'Ophthalmology',      icon: '👁️' },
  { label: 'Psychiatry',         icon: '🧘' },
  { label: 'Urology',            icon: '💧' },
];

export default function FindDoctorPage() {
  const navigate = useNavigate();
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive]   = useState('');
  const [error, setError]     = useState('');

  const runSearch = async (term) => {
    const q = (term || '').trim();
    if (!q) return;
    setActive(q);
    setLoading(true);
    setError('');
    const r = await api.findDoctorsBySpecialization(q);
    if (r.error) setError(r.error);
    setResults(r.data || []);
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearch(query);
  };

  const openDoctor = (d) => {
    const docId = d.doctorId || d.id;
    if (!docId) return;
    navigate(`/book/doctor/${docId}`, {
      state: {
        doctor: d,
        hospital: {
          id: d.hospitalId,
          hospitalId: d.hospitalId,
          name: d.hospitalName,
          city: d.hospitalCity,
        },
      },
    });
  };

  return (
    <div className="fade-in">
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, background: 'rgba(255,255,255,0.98)',
        backdropFilter: 'blur(12px)', zIndex: 10,
      }}>
        <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm" style={{ padding: 8 }}>
          <MdArrowBack size={20} />
        </button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 17 }}>Find Doctor</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Search by specialization</div>
        </div>
      </div>

      <div className="page" style={{ maxWidth: 720 }}>
        <form onSubmit={handleSubmit} className="search-bar" style={{ marginBottom: 20 }}>
          <MdSearch />
          <input
            placeholder="e.g. Dermatology, Cardiology, ENT…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <button type="submit" className="btn btn-primary btn-sm" style={{ marginLeft: 8 }} disabled={loading}>
            {loading ? '…' : 'Search'}
          </button>
        </form>

        {error && (
          <div style={{
            padding: 12, marginBottom: 16, borderRadius: 12,
            background: 'rgba(239,68,68,0.08)', color: '#b91c1c', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <div className="section-title" style={{ fontSize: 14 }}>Browse Specializations</div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 10, marginTop: 10, marginBottom: 24,
        }}>
          {QUICK_SPECS.map(s => (
            <button
              key={s.label}
              type="button"
              onClick={() => { setQuery(s.label); runSearch(s.label); }}
              className="glass glass-p"
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                padding: '14px 8px', cursor: 'pointer', borderRadius: 14,
                border: active === s.label ? '1.5px solid var(--red, #d32f2f)' : '1px solid var(--border)',
                background: active === s.label ? 'rgba(211,47,47,0.06)' : 'var(--bg-card, #fff)',
                transition: 'all 0.18s ease',
              }}
            >
              <div style={{ fontSize: 24 }}>{s.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 700, textAlign: 'center', color: '#1a1a2e' }}>
                {s.label}
              </div>
            </button>
          ))}
        </div>

        {loading && <div className="spinner" />}

        {!loading && results !== null && (
          <>
            <div className="section-title" style={{ fontSize: 14 }}>
              {results.length > 0
                ? `${results.length} doctor${results.length !== 1 ? 's' : ''} for "${active}"`
                : `No matches for "${active}"`}
            </div>

            {results.length === 0 ? (
              <div className="empty">
                <div className="empty-icon">🔍</div>
                <div className="empty-title">No doctors found</div>
                <div>Try Cardiology, Dermatology, General Physician, or another specialty.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                {results.map((d, i) => {
                  const docId = d.doctorId || d.id;
                  const onDuty = d.available !== false && d.isAvailable !== false;
                  return (
                    <div
                      key={`${d.hospitalId}_${docId}_${i}`}
                      onClick={() => openDoctor(d)}
                      className="glass glass-p"
                      style={{ display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer' }}
                    >
                      <div style={{
                        width: 52, height: 52, borderRadius: 14,
                        background: 'rgba(211,47,47,0.08)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 26, flexShrink: 0, overflow: 'hidden',
                      }}>
                        {d.photoUrl
                          ? <img src={d.photoUrl} alt="" style={{ width: 52, height: 52, objectFit: 'cover' }} />
                          : '👨‍⚕️'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>Dr. {d.name}</div>
                        <div style={{
                          fontSize: 12, color: 'var(--red, #d32f2f)',
                          fontWeight: 600, marginBottom: 4,
                        }}>
                          {d.specialization}
                        </div>
                        <div style={{
                          fontSize: 11.5, color: '#64748b',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          🏥 {d.hospitalName}{d.hospitalCity ? ` · ${d.hospitalCity}` : ''}
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                          <span className={`badge ${onDuty ? 'badge-green' : 'badge-red'}`}>
                            {onDuty ? '● On Duty' : '● Off Duty'}
                          </span>
                          {Number(d.rating) > 0 && (
                            <span className="badge badge-amber">⭐ {Number(d.rating).toFixed(1)}</span>
                          )}
                          {d.experience && <span className="badge badge-blue">{d.experience} yrs</span>}
                          {(d.fee || d.consultationFee) && (
                            <span className="badge badge-amber">₹{d.fee || d.consultationFee}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 20, color: 'var(--text-muted)' }}>›</div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
